import { rpc, refreshAdmin, clearToken, RpcError, type AdminInfo } from './rpc.ts'

/**
 * The password is forwarded and forgotten — the useful output is the
 * Set-Cookie the browser stores by itself.
 *
 * Two steps, and the error says which one failed: a `users.login` error is
 * the cloud rejecting the credentials; a `users.admin` error straight after
 * a successful login means the session cookie never made it back.
 */
export async function login(email: string, password: string): Promise<AdminInfo> {
  clearToken()
  let cookie = 'unknown'
  let upstreamStatus = ''
  await rpc('users.login', { email, password }, {
    onResponse: (res) => {
      cookie = res.headers.get('x-rpc-session-cookie') ?? 'unknown'
      upstreamStatus = res.headers.get('x-rpc-upstream-status') ?? ''
    },
  })
  try {
    return await refreshAdmin()
  } catch (err) {
    if (err instanceof RpcError) {
      const why =
        cookie === 'none'
          ? `the cloud accepted the login (HTTP ${upstreamStatus || '?'}) but sent no session cookie`
          : cookie === 'set'
            ? 'a session cookie was issued but the browser did not send it back'
            : 'session check failed straight after login'
      throw new RpcError(err.code, `${why} — ${err.message}`, err.httpStatus, err.retryAfter)
    }
    throw err
  }
}

export async function logout(): Promise<void> {
  try {
    await rpc('users.logout', {})
  } finally {
    clearToken()
  }
}

/** Boot probe: a result means an existing session, a throw means show login. */
export async function currentSession(): Promise<AdminInfo> {
  return await refreshAdmin()
}
